import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWarehouses } from '../../hooks/useWarehouses';
import { Button } from '../ui/Button';
import { CreateWarehouseModal } from './CreateWarehouseModal';
import { formatDimensions } from '../../utils/warehouseUtils';
import type { Warehouse } from '../../types/warehouse';
import {
  Plus,
  Warehouse as WarehouseIcon,
  Map,
  Archive,
  MoreVertical,
  ExternalLink,
  Trash2,
  RotateCcw,
  Loader2,
  AlertCircle,
  Box,
  Layers,
  Grid3X3,
} from 'lucide-react';

export const WarehouseList: React.FC = () => {
  const navigate = useNavigate();
  const { warehouses, loading, error, createWarehouse, archiveWarehouse, fetchWarehouses } = useWarehouses();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedWarehouses, setArchivedWarehouses] = useState<Warehouse[]>([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<string | null>(null);

  const handleOpenWarehouse = (warehouseId: string) => {
    navigate(`/warehouse/${warehouseId}`);
  };

  const handleArchive = async (warehouseId: string) => {
    setArchiving(warehouseId);
    const success = await archiveWarehouse(warehouseId);
    if (success) {
      setActiveMenu(null);
    }
    setArchiving(null);
  };

  const toggleMenu = (warehouseId: string) => {
    setActiveMenu(activeMenu === warehouseId ? null : warehouseId);
  };

  React.useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    if (activeMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeMenu]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Failed to load warehouses
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <Button variant="secondary" onClick={fetchWarehouses}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Warehouse Layouts
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Design and manage your warehouse floor plans with zones and rack positions
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Warehouse
        </Button>
      </div>

      {warehouses.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
          <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Map className="h-10 w-10 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No warehouse layouts yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Create your first warehouse layout to visually organize zones, racks, and bins for efficient inventory management.
          </p>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Warehouse
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {warehouses.map((warehouse) => (
            <WarehouseCard
              key={warehouse.id}
              warehouse={warehouse}
              onOpen={() => handleOpenWarehouse(warehouse.id)}
              onArchive={() => handleArchive(warehouse.id)}
              isArchiving={archiving === warehouse.id}
              menuOpen={activeMenu === warehouse.id}
              onToggleMenu={(e) => {
                e.stopPropagation();
                toggleMenu(warehouse.id);
              }}
            />
          ))}
        </div>
      )}

      <CreateWarehouseModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={createWarehouse}
      />
    </div>
  );
};

interface WarehouseCardProps {
  warehouse: Warehouse;
  onOpen: () => void;
  onArchive: () => void;
  isArchiving: boolean;
  menuOpen: boolean;
  onToggleMenu: (e: React.MouseEvent) => void;
}

const WarehouseCard: React.FC<WarehouseCardProps> = ({
  warehouse,
  onOpen,
  onArchive,
  isArchiving,
  menuOpen,
  onToggleMenu,
}) => {
  return (
    <div
      onClick={onOpen}
      className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-700 hover:-translate-y-1"
    >
      <div className="absolute top-4 right-4">
        <button
          onClick={onToggleMenu}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <MoreVertical className="h-5 w-5" />
        </button>

        {menuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10"
          >
            <button
              onClick={onOpen}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open Warehouse
            </button>
            <button
              onClick={onArchive}
              disabled={isArchiving}
              className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              {isArchiving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Archive className="h-4 w-4" />
              )}
              Archive Warehouse
            </button>
          </div>
        )}
      </div>

      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
          <WarehouseIcon className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate pr-8">
            {warehouse.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatDimensions(warehouse.length, warehouse.width, warehouse.unit)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
        <StatItem icon={Layers} label="Zones" value="-" />
        <StatItem icon={Grid3X3} label="Racks" value="-" />
        <StatItem icon={Box} label="Bins" value="-" />
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Created {new Date(warehouse.created_at).toLocaleDateString()}</span>
          <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400 group-hover:underline">
            Open <ExternalLink className="h-3 w-3" />
          </span>
        </div>
      </div>
    </div>
  );
};

interface StatItemProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string | number;
}

const StatItem: React.FC<StatItemProps> = ({ icon: Icon, label, value }) => (
  <div className="text-center">
    <div className="flex items-center justify-center gap-1 text-gray-400 dark:text-gray-500 mb-1">
      <Icon className="h-3.5 w-3.5" />
    </div>
    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</div>
    <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
  </div>
);
